import axios from "axios";
import { message } from "antd";
import { useCookie, useLocalStorage } from "react-use";
import { useNavigate } from "react-router";
import { showLoader } from "utils/loader";
import config from "./config";

const baseURL = config.apiUrl;
const tokenName = process.env.REACT_APP_TOKEN_NAME || "adminAuthToken";

const extractErrorMsg = (error) => {
  console.log("error", error);
  console.log("error", error.message);
  console.log("response", error.response);
  if (error?.response) {
    if (error?.response?.data) {
      if (error?.response?.data?.message?.message) {
        return error?.response?.data?.message?.message;
      }
      // this is for array of error messages
      if (Array.isArray(error?.response?.data?.message)) {
        return error?.response?.data?.message[0];
      }
      return error?.response?.data?.message;
    }
  } else if (error?.message) {
    return error?.message;
    // if (typeof error?.message == "object") return error?.message;
  } else {
    return error?.response?.statusText || "Network error";
  }
};

export const useBackend = (apiUrl = "") => {
  const [tokenC, updateTokenC, deleteTokenC] = useCookie(tokenName);
  const [tokenL, updateTokenL, deleteTokenL] = useLocalStorage(tokenName);
  const navigate = useNavigate();

  const backend = (needLoader = false) => {
    const nest = axios.create({ baseURL: apiUrl || baseURL });
    try {
      // Add a request interceptor
      nest.interceptors.request.use(
        function (config) {
          // Do something before request is sent
          if (config.loader) {
            // if (needLoader) {
            showLoader(true);
            // window.showLoader = true
          }
          if (config.msg) {
            if (config.msg.loading) {
              config.msg.id = message.loading({
                content: config.msg.loading,
                key: "current",
              });
            }
          }
          if (getToken()) {
            config.headers = { Authorization: `Bearer ${getToken()}` };
          }
          return config;
        },
        function (error) {
          // Do something with request error
          return Promise.reject(error);
        }
      );

      // Add a response interceptor
      nest.interceptors.response.use(
        function (response) {
          // Any status code that lie within the range of 2xx cause this function to trigger
          // Do something with response data
          if (response.config.msg) {
            let msg = response?.config?.msg?.success || response?.data?.message;
            if (response?.config?.msg?.id) {
              message.success({ content: msg, key: "current" });
            } else {
              message.success(msg);
            }
          }
          // if (response?.data?.token) {
          //   storeToken(response?.data?.token)
          // }

          if (response?.config?.loader) {
            // if (needLoader) {
            showLoader(false);
            // window.showLoader = false
          }
          return response?.data || response;
        },
        function (error) {
          // Any status codes that falls outside the range of 2xx cause this function to trigger
          // Do something with response error
          let msg = extractErrorMsg(error);
          if (error?.response?.config?.msg) {
            if (error?.response?.config?.msg?.id) {
              message.error({ content: msg, key: "current" });
            }
          } else if (error?.config?.msg) {
            if (error?.config?.msg?.id) {
              message.error({ content: msg, key: "current" });
            }
          } else {
            message.error(msg);
          }
          if (
            error?.response?.status === 401 &&
            window.location.href.search("auth") === -1
          ) {
            window.location.href = "/auth/login";
          }
          showLoader(false);
          return Promise.reject(error);
        }
      );
    } catch (error) {
      // message.error(message);
      console.log(error);
    }
    return nest;
  };

  const updateToken = (token, remember) => {
    updateTokenL(token);
    remember === true && updateTokenC(token);
  };

  const removeToken = (token) => {
    deleteTokenL(token);
    deleteTokenC(token);
  };

  const getToken = () => {
    return tokenL || tokenC || null;
  };

  /**
   *
   * @param {Endpoint Name} endpoint
   * @param {Uid} id
   * @returns
   */
  const getItem = (endpoint, id, config = {}) =>
    new Promise((resolve) => {
      // let config = {
      //   loader, msg: { loading: "Registering...", success: "Registered successfully." }
      // }
      backend()
        .get(`/${endpoint}/${id}`, config)
        .then((data) => {
          return resolve(data);
        })
        .catch((error) => {
          console.log(error);
        });
    });

  /**
   *
   * @param {Endpoint Name} endpoint
   * @param {Uid} id
   * @returns
   */
  const deleteItem = (endpoint, id, config = {}) =>
    new Promise((resolve) => {
      // let config = {
      //   loader: true, msg: { loading: "Deleting...", success: "Deleted successfully." }
      // }
      backend()
        .delete(`/${endpoint}/${id}`, config)
        .then((data) => {
          if (config?.redirectTo) {
            navigate(`/${config?.redirectTo}`);
          }
          return resolve(data);
        })
        .catch((error) => {
          console.log(error);
        });
    });

  const updateItem = (endpoint, id, payload, config) =>
    new Promise((resolve) => {
      backend()
        .put(`/${endpoint}/${id}`, payload, config)
        .then((data) => {
          if (config?.redirectTo) {
            navigate(`/${config?.redirectTo}`);
          }

          return resolve(data);
        })
        .catch((error) => {
          console.log(error);
        });
    });

  /**
   *
   * @param {*} endpoint
   * @param {*} query
   * @param {*} config
   * @returns
   */
  const getList = (endpoint, query = {}, config = {}) =>
    new Promise((resolve) => {
      const defaultQuery = { limit: 50 };
      // let config = {
      //   loader, msg: { loading: "Registering...", success: "Registered successfully." }
      // }
      const params = new URLSearchParams({
        ...defaultQuery,
        ...query,
      }).toString();

      backend()
        .get(`/${endpoint}?${params}`, config)
        .then((data) => {
          return resolve(data);
        })
        .catch((error) => {
          console.log(error);
        });
    });

  /**
   *
   * @param {*} endpoint
   * @param {*} searchBy
   * @param {*} searchValue
   * @param {*} otherConditions
   * @param {*} config
   * @returns
   */
  const searchItems = (
    endpoint,
    searchBy,
    searchValue,
    otherConditions = {},
    config = {}
  ) =>
    new Promise((resolve) => {
      const defaultQuery = { limit: 50 };
      // let config = {
      //   loader, msg: { loading: "Registering...", success: "Registered successfully." }
      // }
      const params = new URLSearchParams({
        ...defaultQuery,
        [searchBy]: searchValue + "\uf8ff",
        ...otherConditions,
      }).toString();

      backend()
        .get(`/${endpoint}?${params}`, config)
        .then(({ items }) => {
          return resolve(items);
        })
        .catch((error) => {
          console.log(error);
        });
    });

  /**
   *
   * @param {*} endpoint
   * @param {*} payload
   * @param {*} config
   * @returns
   */
  const addItem = (endpoint, payload, config = {}) =>
    new Promise((resolve) => {
      backend()
        .post(`/${endpoint}`, payload, config)
        .then((data) => {
          if (config?.redirectTo) {
            navigate(`/${config?.redirectTo}`);
          }
          return resolve(data);
        })
        .catch((error) => {
          console.log(error);
        });
    });

  /**
   *
   * @param {*} endpoint
   * @param {*} id
   * @param {*} payload
   * @param {*} config
   * @returns
   */
  const uploadFile = (endpoint, id, payload, config = {}) =>
    new Promise((resolve) => {
      config.headers = { "Content-Type": "multipart/form-data" };
      backend()
        .post(`/${endpoint}/file/${id}`, payload, config)
        .then((data) => {
          if (config?.redirectTo) {
            navigate(`/${config?.redirectTo}`);
          }

          return resolve(data);
        })
        .catch((error) => {
          console.log(error);
        });
    });

  /**
   *
   * @param {string} endpoint
   * @param {string} id
   * @param {object} config
   * @returns
   */
  const deleteFile = (endpoint, id, config = {}) =>
    new Promise((resolve) => {
      backend()
        .delete(`/${endpoint}/file/${id}`, config)
        .then((data) => {
          if (config?.redirectTo) {
            navigate(`/${config?.redirectTo}`);
          }

          return resolve(data);
        })
        .catch((error) => {
          console.log(error);
        });
    });

  return {
    baseURL: apiUrl || baseURL,
    backend,
    getToken,
    updateToken,
    removeToken,

    getItem,
    getList,
    searchItems,
    addItem,
    deleteItem,
    updateItem,
    uploadFile,
    deleteFile,
  };
};
